/**
 * TKP-184 md.80 / md.76 — tek seçim listesi: organizasyon sözlüğü + kütüphane (saf yardımcılar; store/Vue yok).
 * Veri ve ilgili kişi ekleme panellerinde "Kütüphaneden seç" ve "Organizasyondan seç" sekmeleri tek sekmeye indi: arama
 * iki kaynağı birlikte sunar, organizasyonda karşılığı olan kütüphane kalemi ikinci kez listelenmez. Kütüphaneden gelen
 * seçenek `fromLibrary: true` taşır — kaydederken "kütüphaneden ekle" ucu kullanılır (organizasyon karşılığı o anda açılır).
 * Aynı kural "süreç bazında kategori" seçicisinde de geçerli (md.76: kütüphane kategorileri listede yoktu).
 */
const idOf = (v) => String((v && v._id) || v || "");
const norm = (v) => String(v || "").trim().toLocaleLowerCase("tr");

/** İki yerelleştirilmiş ad aynı mı (ortak bir dilde, boşluk ve büyük/küçük harf duyarsız). */
export const sameName = (a, b) => !!a && !!b && Object.keys(a).some((k) => a[k] && b[k] && norm(a[k]) === norm(b[k]));

/** TKP-230: seçimde aynı adı taşıyan ikinci kayıt (kütüphane kalemi + organizasyon verisi dahil) tutulmaz — ilk gelen kalır. */
export const uniqueByName = (list = []) => (list || []).filter(Boolean).reduce((out, d) => (d.__new || !out.some((x) => !x.__new && sameName(x.name, d.name)) ? [...out, d] : out), []);

/** TKP-230: listede verilen adı (kullanıcının dilinde) taşıyan kayıt — "Yeni veri" var olanı ikinci kez açmasın. */
export const findSameName = (list = [], text, language = "tr") => {
  const name = { [language || "tr"]: String(text || "").trim() };
  return (list || []).find((d) => d && d._id && !d.__new && sameName(d.name, name)) || null;
};

/** Arama isteği: ada göre sıralı (kullanıcının dilinde), 50 ile sınırlı değil — liste "eksik" görünmesin (md.77). */
export const searchRequest = (value, language = "tr", limit = 1000) => ({ options: { limit, sort: { [`name.${language || "tr"}`]: 1 } }, query: { name: { value: value || "" } } });

/**
 * Organizasyon kayıtları önce; ardından organizasyonda karşılığı OLMAYAN kütüphane kalemleri (`fromLibrary: true`).
 * Karşılık: kütüphane bağı (`libraryItem`) ya da aynı ad (eski kayıtlarda bağ yok). Kütüphanede aynı adı taşıyan birden çok
 * kalem (sistem + çalışma alanı kopyası) TEK seçenek olur (ilki): organizasyon sözlüğünde ad zaten tekildir, ikisi birden eklenemez.
 */
export const mergeOrgAndLibrary = (orgDocs = [], libraryDocs = []) => {
  const org = (orgDocs || []).filter((d) => d && d._id);
  const linked = new Set(org.map((d) => idOf(d.libraryItem)).filter(Boolean));
  // TKP-200 m.12: ad karşılaştırması küme ile (dil:ad) — eskiden her kalem için listeyi baştan tarıyordu (1000×1000, panel donuyordu)
  const keysOf = (name) => Object.keys(name || {}).filter((k) => name[k]).map((k) => `${k}:${norm(name[k])}`);
  const taken = new Set(org.flatMap((d) => keysOf(d.name)));
  const seen = new Set();
  const fresh = [];
  for (const l of libraryDocs || []) {
    if (!l || !l._id || linked.has(idOf(l)) || seen.has(idOf(l))) continue;
    const keys = keysOf(l.name);
    if (keys.some((k) => taken.has(k))) continue;
    seen.add(idOf(l));
    keys.forEach((k) => taken.add(k));
    fresh.push(l);
  }
  return [...org, ...fresh.map((l) => ({ ...l, fromLibrary: true }))];
};

/**
 * TKP-200 m.12: seçim kutusu her tuşta iki büyük istek atıyordu — liste panel başına BİR kez yüklenir, yazılan istemcide süzülür.
 * Yükleme sınırı doldurduysa (liste eksik olabilir) yazılan için sunucu araması yapılır. `load(value)` birleşik listeyi döner.
 */
export const cachedPicker = (load, limit = 1000) => {
  let full = null;
  return async (value) => {
    if (!full) full = Promise.resolve(load("")).then((list) => ({ list, truncated: list.length >= limit }));
    const { list, truncated } = await full;
    const q = norm(value);
    if (!q) return list;
    if (truncated) return load(value);
    return list.filter((d) => Object.values(d.name || {}).some((n) => norm(n).includes(q)));
  };
};

/** TKP-200 m.13: kütüphanede ve sözlükte olmayan ad için "yeni oluştur" seçeneği (aynı ad listede varsa eklenmez). */
export const NEW_ENTRY_PREFIX = "__new__:";
/** Listede olmayan ad için yeni kayıt (seçimde gösterilen metin `label(ad)`; kayıtta `__new` adı açılır). */
export const newEntry = (value, label = (q) => q) => {
  const q = String(value || "").trim();
  if (!q) return null;
  const text = label(q);
  return { _id: `${NEW_ENTRY_PREFIX}${q}`, name: { tr: text, en: text, de: text, fr: text }, __new: q };
};
export const withCreateOption = (docs = [], value, label = (q) => q) => {
  const q = String(value || "").trim();
  if (!q || (docs || []).some((d) => Object.values((d && d.name) || {}).some((n) => norm(n) === norm(q)))) return docs || [];
  return [newEntry(q, label), ...(docs || [])];
};
/**
 * TKP-218: görünür "+ Yeni … ekle" bağlantısından gelen ad seçime yeni kayıt olarak eklenir — aramadaki "➕ Yeni … oluştur" ile
 * aynı kayıt. Boş ad ve seçimde aynı adı taşıyan kayıt (yeni ya da mevcut) eklenmez.
 */
export const addNewEntry = (selection = [], value, label = (q) => q) => {
  const list = Array.isArray(selection) ? selection.filter(Boolean) : selection ? [selection] : [];
  const entry = newEntry(value, label);
  if (!entry) return list;
  const taken = list.some((d) => (d.__new && norm(d.__new) === norm(entry.__new)) || Object.values(d.name || {}).some((n) => norm(n) === norm(entry.__new)));
  return taken ? list : [...list, entry];
};
export const isNewEntry = (item) => !!(item && item.__new);

export const isLibraryPick = (item) => !!(item && item.fromLibrary);

/** Kayıt sonucu: taslak / kaynağı eklendi (md.75 birleşim) / açıldı / zaten var (409) / hata. */
export const classifySave = (response, error) => {
  if (error) return error.response && error.response.status === 409 ? "exists" : "failed";
  const data = (response && response.data) || {};
  // TKP-236 n.2: axios kesicisi taslak yanıtını kayıt biçimine çeviriyor (`__draft`); yalnız ham `draft`a bakınca taslak "eklendi" sayılıyordu
  if (data.draft || data.__draft) return "draft";
  return data.merged ? "linked" : "created";
};

/**
 * TKP-203: kaydedilen düğüm/satırın kimliği — doğrudan kayıtta belgenin, taslakta gölgenin; bağlanan (mevcut) kayıt yok sayılır.
 * Taslak yanıtını axios kesicisi kayıt biçimine çevirir (`_id` = gölge kimliği, `__draft`); ham biçim (`edit.entityId`) de tanınır.
 */
export const savedIdOf = (response) => {
  const data = (response && response.data) || {};
  if (data.draft) return (data.edit && data.edit.entityId && String(data.edit.entityId)) || null;
  return !data.merged && data._id ? String(data._id) : null;
};

export const summarizeSaves = (results = []) =>
  results.reduce(
    (s, r) => {
      s[["created", "draft", "linked", "exists"].includes(r.status) ? r.status : "failed"]++;
      return s;
    },
    { created: 0, draft: 0, linked: 0, exists: 0, failed: 0 }
  );

/**
 * Seçilen kategori kütüphaneden geldiyse organizasyon karşılığına çevirir (bul ya da aç); organizasyon kategorisi olduğu gibi döner.
 * `dispatch`: Vuex dispatch; `process`: yetki bağlamı (sürecin düğümünü düzenleyebilen çağırabilir).
 */
export const resolveNodeCategory = async (dispatch, category, process) => {
  if (!isLibraryPick(category)) return category;
  const { fromLibrary, ...libraryItem } = category;
  const response = await dispatch("organizationDataCategory/addFromLibrary", { libraryItem, process: { _id: idOf(process) } });
  return response && response.data;
};

/**
 * TKP-221: başka sekmede seçili (yeni girilenler dahil, henüz kaydedilmemiş) kayıtlar seçicinin başında — aramaya uyanlar,
 * listede aynı kimlikle zaten olanlar tekrar edilmez.
 */
export const pendingFirst = (list = [], pending = [], value = "") => {
  const q = norm(value);
  const ids = new Set((list || []).map((d) => String(d && d._id)));
  const hits = (pending || []).filter((p) => p && p._id && !ids.has(String(p._id)) && (!q || Object.values(p.name || {}).some((n) => norm(n).includes(q)) || (p.__new && norm(p.__new).includes(q))));
  // kullanıcı (2026-10-16): yeni girilen kayıt "➕ Yeni … oluştur" değil, kayıtlı kayıtlar gibi düz adıyla görünsün (`__new` korunur: seçilince açılır)
  const plain = (p) => (p.__new ? { ...p, name: { tr: p.__new, en: p.__new, de: p.__new, fr: p.__new } } : p);
  return [...hits.map(plain), ...(list || [])];
};
